import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Plus, Trash2, Eye, Download, Search, Inbox, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Shared document module for Invoices and Quotations.
 */
export default function DocumentModule({
  title,
  subtitle,
  endpoint,           // 'invoices' or 'quotations'
  numberKey,          // 'invoice_number' or 'quotation_number'
  dateKey,            // 'invoice_date' or 'quotation_date'
  pageTestId,
  showConvert = false,
}) {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [biz, setBiz] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return {
      customer_id: "",
      customer_name: "",
      customer_phone: "",
      customer_gst: "",
      customer_address: "",
      items: [{ name: "", quantity: 1, rate: 0, gst_percent: 18, hsn: "" }],
      discount: 0,
      extra_charges: 0,
      notes: "",
      terms: "",
    };
  }

  const load = async () => {
    setLoading(true);
    const [a, c, p, s, b] = await Promise.all([
      api.get(`/${endpoint}`),
      api.get(`/customers`),
      api.get(`/products`),
      api.get(`/services`),
      api.get(`/business`).catch(() => ({ data: null })),
    ]);
    setItems(a.data);
    setCustomers(c.data);
    setProducts(p.data);
    setServices(s.data);
    setBiz(b.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [endpoint]);

  const totals = (() => {
    let subtotal = 0, tax = 0;
    form.items.forEach((it) => {
      const lt = (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0);
      subtotal += lt;
      tax += lt * ((parseFloat(it.gst_percent) || 0) / 100);
    });
    const grand = subtotal + tax - (parseFloat(form.discount) || 0) + (parseFloat(form.extra_charges) || 0);
    return { subtotal, tax, grand };
  })();

  const onCustomerSelect = (id) => {
    const c = customers.find((x) => x.id === id);
    if (c) setForm({ ...form, customer_id: id, customer_name: c.name, customer_phone: c.phone || "", customer_gst: c.gst_number || "", customer_address: c.billing_address || "" });
  };

  const onItemProductSelect = (idx, pid) => {
    const p = products.find((x) => x.id === pid) || services.find((x) => x.id === pid);
    if (!p) return;
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      product_id: pid,
      name: p.name,
      rate: p.selling_price || p.price || 0,
      gst_percent: p.gst_percent || 18,
      hsn: p.hsn || "",
    };
    setForm({ ...form, items });
  };

  const updateItem = (idx, key, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [key]: key === "name" || key === "hsn" ? val : (val === "" ? "" : parseFloat(val) || 0) };
    setForm({ ...form, items });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", quantity: 1, rate: 0, gst_percent: 18, hsn: "" }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!form.customer_name) return toast.error("Customer required");
    if (form.items.length === 0 || !form.items[0].name) return toast.error("Add at least one item");
    try {
      await api.post(`/${endpoint}`, form);
      toast.success("Saved");
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/${endpoint}/${id}`);
    load();
  };

  const convert = async (id) => {
    try {
      await api.post(`/quotations/${id}/convert`);
      toast.success("Converted to invoice");
      load();
    } catch {
      toast.error("Convert failed");
    }
  };

  const filtered = search
    ? items.filter((i) => JSON.stringify(i).toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="space-y-6" data-testid={pageTestId}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="text-xs sm:text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="flex gap-2 items-center w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-full sm:w-56" data-testid={`${pageTestId}-search`} />
          </div>
          <Button onClick={() => { setForm(emptyForm()); setOpen(true); }} data-testid={`${pageTestId}-add-btn`}>
            <Plus size={16} className="mr-1" /> <span className="hidden sm:inline">New {title.replace(/s$/, "")}</span><span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Inbox size={36} className="mx-auto text-muted-foreground" />
            <div className="mt-4 text-sm text-muted-foreground">No documents yet. Click &apos;New&apos; to create one.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[700px]">
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => (
                <TableRow key={it.id} data-testid={`${pageTestId}-row-${it.id}`}>
                  <TableCell><span className="font-medium">{it[numberKey]}</span></TableCell>
                  <TableCell>{it.customer_name || "—"}</TableCell>
                  <TableCell>{(it[dateKey] || it.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="font-semibold">{inr(it.grand_total)}</TableCell>
                  <TableCell>
                    <Badge variant={it.status === "paid" ? "default" : it.status === "converted" ? "secondary" : "outline"}>
                      {it.status || "draft"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => setPreview(it)} data-testid={`view-${it.id}`}>
                      <Eye size={14} />
                    </Button>
                    {showConvert && it.status !== "converted" && (
                      <Button variant="ghost" size="icon" onClick={() => convert(it.id)} data-testid={`convert-${it.id}`}>
                        <ArrowRight size={14} />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(it.id)} data-testid={`delete-${it.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">New {title.replace(/s$/, "")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-5 mt-2">
            <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label>Customer</Label>
                <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={form.customer_id || ""} onChange={(e) => onCustomerSelect(e.target.value)} data-testid="select-customer">
                  <option value="">— Select or type below —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Customer Name</Label>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="doc-customer-name" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
              </div>
              <div>
                <Label>Customer GST</Label>
                <Input value={form.customer_gst} onChange={(e) => setForm({ ...form, customer_gst: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Address</Label>
                <Input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Items</Label>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="add-line-item"><Plus size={14} /> Add line</Button>
              </div>
              <div className="border border-border rounded-lg overflow-x-auto">
                <Table className="min-w-[720px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-14">No</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead className="w-20">HSN</TableHead>
                      <TableHead className="w-20">Qty</TableHead>
                      <TableHead className="w-24">Rate</TableHead>
                      <TableHead className="w-20">GST%</TableHead>
                      <TableHead className="text-right w-24">Amount</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.items.map((it, idx) => {
                      const amt = (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0);
                      return (
                        <TableRow key={idx}>
                          <TableCell className="text-muted-foreground text-sm">{idx + 1}</TableCell>
                          <TableCell>
                            <Input value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} placeholder="Item name" data-testid={`item-name-${idx}`} />
                            <textarea
                              value={it.description || ""}
                              onChange={(e) => updateItem(idx, "description", e.target.value)}
                              placeholder="Enter Description (optional)"
                              rows={2}
                              className="w-full mt-1 text-xs rounded border bg-background px-2 py-1.5 resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                              data-testid={`item-desc-${idx}`}
                            />
                            <select className="text-xs mt-1 h-7 w-full rounded border bg-background px-2" onChange={(e) => onItemProductSelect(idx, e.target.value)} defaultValue="">
                              <option value="">Quick select from catalog…</option>
                              <optgroup label="Products">
                                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </optgroup>
                              <optgroup label="Services">
                                {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </optgroup>
                            </select>
                          </TableCell>
                          <TableCell><Input value={it.hsn || ""} onChange={(e) => updateItem(idx, "hsn", e.target.value)} placeholder="-" /></TableCell>
                          <TableCell><Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} /></TableCell>
                          <TableCell><Input type="number" value={it.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} /></TableCell>
                          <TableCell><Input type="number" value={it.gst_percent} onChange={(e) => updateItem(idx, "gst_percent", e.target.value)} /></TableCell>
                          <TableCell className="text-right font-medium">{inr(amt)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => removeItem(idx)}><Trash2 size={14} className="text-destructive" /></Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground md:hidden">Swipe the table sideways to see all columns</div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Notes</Label>
                <textarea className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-background" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div>
                <Label>Terms & Conditions</Label>
                <textarea className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-background" rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
              </div>
              <div>
                <Label>Discount (₹)</Label>
                <Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: parseFloat(e.target.value) || 0 })} data-testid="doc-discount" />
              </div>
              <div>
                <Label>Extra Charges (₹)</Label>
                <Input type="number" value={form.extra_charges} onChange={(e) => setForm({ ...form, extra_charges: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>

            <Card className="p-4 bg-muted/30">
              <div className="space-y-1.5 text-sm">
                <Row label="Subtotal" value={inr(totals.subtotal)} />
                <Row label="Tax (CGST + SGST / IGST)" value={inr(totals.tax)} />
                <Row label="Discount" value={"-" + inr(form.discount || 0)} />
                <Row label="Extra Charges" value={inr(form.extra_charges || 0)} />
                <div className="border-t pt-2 mt-2 flex justify-between font-heading font-bold text-lg">
                  <span>Grand Total</span>
                  <span data-testid="doc-grand-total">{inr(totals.grand)}</span>
                </div>
              </div>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="doc-save-btn">Save {title.replace(/s$/, "")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto print:shadow-none">
          {preview && <DocPreview doc={preview} biz={biz} numberKey={numberKey} dateKey={dateKey} kind={title.replace(/s$/, "")} />}
          <DialogFooter className="print:hidden">
            <Button variant="outline" onClick={() => window.print()} data-testid="doc-print-btn"><Download size={14} className="mr-1" /> Print / PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value}</span></div>;
}

function DocPreview({ doc, biz, numberKey, dateKey, kind }) {
  const upi = biz?.upi_id || "";
  const qrUrl = upi
    ? `https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(`upi://pay?pa=${upi}&pn=${biz?.name || ""}&am=${doc.grand_total || 0}&cu=INR`)}`
    : "";
  return (
    <div className="bg-white text-slate-900 p-8" id="doc-preview" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="flex items-start justify-between border-b pb-6 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">{kind}</div>
          <div className="font-bold text-2xl mt-1" style={{ fontFamily: "Outfit" }}>{biz?.name || "Your Business"}</div>
          <div className="text-xs text-slate-500 mt-1">{biz?.address}</div>
          <div className="text-xs text-slate-500">GSTIN: {biz?.gst_number} · {biz?.phone}</div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold">{doc[numberKey]}</div>
          <div className="text-xs text-slate-500">{(doc[dateKey] || "").slice(0, 10)}</div>
          <div className={`mt-2 inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${doc.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>{doc.status}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
        <div>
          <div className="text-xs uppercase text-slate-500 font-semibold tracking-wider mb-1">Billed To</div>
          <div className="font-semibold">{doc.customer_name}</div>
          <div className="text-xs text-slate-500">{doc.customer_address}</div>
          <div className="text-xs text-slate-500">{doc.customer_phone}</div>
          {doc.customer_gst && <div className="text-xs text-slate-500">GSTIN: {doc.customer_gst}</div>}
        </div>
        <div className="text-right">
          {qrUrl && (
            <>
              <img src={qrUrl} alt="UPI QR" className="ml-auto" />
              <div className="text-xs text-slate-500 mt-1">Scan to pay · {upi}</div>
            </>
          )}
        </div>
      </div>

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-y bg-slate-50">
            <th className="text-left p-2">#</th>
            <th className="text-left p-2">Item</th>
            <th className="text-left p-2">HSN</th>
            <th className="text-right p-2">Qty</th>
            <th className="text-right p-2">Rate</th>
            <th className="text-right p-2">GST%</th>
            <th className="text-right p-2">Amount</th>
          </tr>
        </thead>
        <tbody>
          {doc.items?.map((it, i) => (
            <tr key={i} className="border-b">
              <td className="p-2 align-top">{i + 1}</td>
              <td className="p-2 align-top">
                <div className="font-medium">{it.name}</div>
                {it.description && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{it.description}</div>}
              </td>
              <td className="p-2 align-top">{it.hsn || "—"}</td>
              <td className="p-2 text-right align-top">{it.quantity}</td>
              <td className="p-2 text-right align-top">{inr(it.rate)}</td>
              <td className="p-2 text-right align-top">{it.gst_percent || 0}%</td>
              <td className="p-2 text-right font-medium align-top">{inr(it.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end mt-4">
        <div className="w-72 text-sm space-y-1">
          <Row2 label="Subtotal" value={inr(doc.subtotal)} />
          <Row2 label="CGST" value={inr(doc.cgst)} />
          <Row2 label="SGST" value={inr(doc.sgst)} />
          {doc.igst > 0 && <Row2 label="IGST" value={inr(doc.igst)} />}
          {doc.discount > 0 && <Row2 label="Discount" value={"-" + inr(doc.discount)} />}
          {doc.extra_charges > 0 && <Row2 label="Extra Charges" value={inr(doc.extra_charges)} />}
          <div className="border-t pt-2 mt-2 flex justify-between font-bold text-base">
            <span>Grand Total</span><span>{inr(doc.grand_total)}</span>
          </div>
        </div>
      </div>

      {doc.notes && <div className="mt-6 text-xs"><span className="font-semibold">Notes: </span>{doc.notes}</div>}
      {doc.terms && <div className="mt-2 text-xs"><span className="font-semibold">Terms: </span>{doc.terms}</div>}

      <div className="mt-10 grid grid-cols-2 text-xs text-slate-500">
        <div>
          <div className="font-semibold text-slate-700 mb-2">Bank Details</div>
          {biz?.bank_name && <div>{biz.bank_name}</div>}
          {biz?.account_number && <div>A/C: {biz.account_number}</div>}
          {biz?.ifsc && <div>IFSC: {biz.ifsc}</div>}
        </div>
        <div className="text-right">
          <div className="mt-6 border-t pt-2 inline-block">Authorised Signatory</div>
        </div>
      </div>
    </div>
  );
}

function Row2({ label, value }) {
  return <div className="flex justify-between"><span className="text-slate-500">{label}</span><span>{value}</span></div>;
}
